import { isExactCodeMatch } from '../utils/exactCodeMatch';
import ApiWithLogging from '../utils/apiWithLogging';
import type { RentalItemDepositFields } from './depositService';
import { getLocalDateString } from '../utils/dateUtils';

/** อุปกรณ์ของสัญญาที่ระบบยังถือว่าอยู่กับลูกค้า (ยังไม่มีใบตามเก็บสถานะ "รับคืนแล้ว") */
/** สิทธิ์แก้วันสิ้นสุดเก็บเงินหลังสัญญาจบ (feedback amintra 29 ก.ย. 2026 RNT-20260716-0051) */
export const BILLING_MANAGE_PERMISSION = 'rentals.billing_manage';

export interface RentalBillingEndDateResponse {
  rental_id: number;
  rental_number: string;
  status: string;
  old_billing_end_date: string | null;
  billing_end_date: string | null;
  /** false = ค่าใหม่เท่าค่าเดิม ไม่มีอะไรถูกเขียน */
  changed: boolean;
}

export interface OutstandingReturnItem {
  rental_item_id: number;
  device_id: number | null;
  sim_id: number | null;
  label: string;
  product_name: string | null;
  return_item_id: number | null;
  return_item_status: string | null;
  expected_return_date: string | null;
  /** ค้างมากี่วันแล้ว (null = ไม่มีวันอ้างอิง) */
  days_outstanding: number | null;
}

export interface OutstandingReturns {
  rental_id: number;
  outstanding_count: number;
  items: OutstandingReturnItem[];
}

// Fix ก (20 ก.ย. 2026) — สัญญา "รอการยกเลิก" ที่รับของคืนครบแล้ว: ประเมินว่าปิดใบแจ้งยกเลิกได้เลยไหม
// (ของที่คืนแล้วสถานะขึ้น "พร้อมใช้งาน" แต่ picker ยังหาไม่เจอ เพราะสัญญายังนับว่าถือของอยู่จนถึง
// วันที่แจ้งยกเลิกไว้ — ปิดใบแจ้งยกเลิกทันทีคือทางที่ปลดล็อกโดยไม่กระทบวันคิดเงิน)
// reason_code ตัวที่ FE ยังไม่ได้ใช้แยกกรณี (NO_TRACKED_GOODS ฯลฯ) — ปฏิบัติแบบเดียวกันเสมอ:
// eligible=false ⇒ ไม่มี banner ไม่ต้อง switch ตาม reason_code เอง (Addenda: FE treat any eligible=false as "no banner")
export type CancellationFinalizeReasonCode =
  | 'ELIGIBLE'
  | 'ELIGIBLE_NOT_DELIVERED'
  | 'NOT_PENDING_CANCELLATION'
  | 'NO_OPEN_NOTICE'
  | 'GOODS_OUTSTANDING'
  | 'NO_TRACKED_GOODS';

export interface CancellationFinalizeStatus {
  rental_id: number;
  rental_number: string | null;
  rental_status: string;
  eligible: boolean;
  reason_code: CancellationFinalizeReasonCode;
  message: string;
  notice_id: number | null;
  notice_status: string | null;
  planned_cancellation_date: string | null;
  billing_end_date: string | null;
  returned_count: number;
  outstanding: string[];
  /** ชิ้นที่ปิดรายการเป็น "ของหาย" แล้ว — ไม่ใช่คืนสำเร็จ แต่ไม่ค้าง (29 ต.ค. 2026) · backend เก่าไม่ส่ง = [] */
  lost?: string[];
  deposit_to_refund: { count: number; amount: number };
  outstanding_invoices: string[];
  can_finalize: boolean;
  blocked_reason: string | null;
  /** BUG-S2259-002: ชิ้นที่รับคืนแล้วแต่ไม่กลับเข้าคลัง (ชำรุด/หาย/ระงับ) — backend เก่าไม่ส่ง = ถือว่าว่าง */
  not_usable?: { kind: string; label: string; status_label: string }[];
}

export interface CancellationFinalizeReleasedItem {
  kind: 'device' | 'sim';
  label: string;
  status: string;
  status_label: string;
  /** Addenda: ตอนนี้เลือกได้ในงานใหม่แล้ว */
  usable: boolean;
}

export interface CancellationFinalizeResult {
  rental_id: number;
  rental_number: string | null;
  rental_status: 'CANCELLED';
  notice_id: number;
  billing_end_date: string | null;   // ไม่เปลี่ยน — ยังเป็นวันที่แจ้งไว้เดิม
  released: CancellationFinalizeReleasedItem[];
  deposit_to_refund: { count: number; amount: number };
  message: string;
}

// สถานะการเช่า - ตรงกับ Backend enum values
export enum RentalStatus {
  DRAFT = 'DRAFT',                           // ร่าง/รอดำเนินการ
  ACTIVE = 'ACTIVE',                         // เช่าใช้อยู่
  PENDING_CANCELLATION = 'PENDING_CANCELLATION', // รอการยกเลิก
  COMPLETED = 'COMPLETED',                   // เสร็จสิ้น/คืนแล้ว
  CANCELLED = 'CANCELLED'                    // ยกเลิกแล้ว
}

// ประเภทการเช่า
export enum RentalType {
  RENT = 'RENT',         // เช่า
  PURCHASE = 'PURCHASE', // ซื้อ
  TRIAL = 'TRIAL'        // ทดลองใช้
}

// รายการอุปกรณ์ในการเช่า (rental_items) — ฟิลด์มัดจำทั้งหมด (รวม Deposit v2 + computed) มาจาก RentalItemDepositFields
export interface RentalItem extends RentalItemDepositFields {
  id: number;
  product_id?: number | null;
  device_type_id?: number | null;
  device_id?: number | null;
  sim_id?: number | null;
  quantity: number;
  rental_rate?: number;
  rental_period_days?: number;
  is_software_only?: boolean;
  notes?: string;
  // Remote Access (AnyDesk) — ค่าเดียวของรอบเช่า แก้ได้หลายหน้าจอผ่าน remoteAccessService (23 ก.ย. 2026)
  anydesk_id?: string | null;
  remote_access_notes?: string | null;
  // Flat backend fields (when API does not include relation)
  product_name?: string;
  device_code?: string;
  sim_number?: string;
  // Nested relations (เมื่อ backend include)
  device?: { id: number; code?: string; serial_number?: string; model?: string; name?: string };
  product?: { id: number; name: string; code?: string };
  sim?: { id: number; phone_number?: string; sim_number?: string };
}

// ประเภทข้อมูลการเช่า
export interface Rental {
  id: number;
  code: string;                  // รหัสการเช่า
  rental_number?: string;        // หมายเลขการเช่า
  rental_type: RentalType;       // ประเภทการเช่า (เช่า/ซื้อ/ทดลองใช้)
  rental_date: string;           // วันที่เช่า
  billing_start_date?: string;   // วันที่เริ่มเก็บเงิน (สำหรับกรณีมีช่วงทดลองใช้ฟรี)
  billing_end_date?: string;     // วันสิ้นสุดเก็บเงิน (single source of truth สำหรับ proration)
  prepaid_until?: string | null; // ชำระล่วงหน้าถึงวันที่ — วางบิลข้ามรอบที่ครอบคลุม (NULL = ไม่ได้ชำระล่วงหน้า)
  prepaid_note?: string | null;  // หมายเหตุการชำระล่วงหน้า
  period_days: number;           // ระยะเวลาการเช่า (วัน)
  period_months?: number;        // ระยะเวลาการเช่า (เดือน)
  end_date: string;              // วันสิ้นสุดการเช่า (Alias สำหรับ expected_return_date)
  expected_return_date?: string; // วันที่คาดว่าจะคืน (ตรงกับ Backend)
  actual_return_date?: string;   // วันที่คืนจริง
  
  // ข้อมูลความสัมพันธ์
  site_id: number;               // รหัสโครงการ
  site_name?: string;            // ชื่อโครงการ (จาก backend)
  customer_id?: number;          // รหัสลูกค้า
  customer_name?: string;        // ชื่อลูกค้า (จาก backend)
  company_id: number;            // รหัสบริษัท
  company_name?: string;         // ชื่อบริษัท (จาก backend)
  company?: {                    // ข้อมูลบริษัทเต็ม (เมื่อ backend include relations)
    id: number;
    name: string;
    tax_id?: string;
  };
  site?: {                       // ข้อมูลโครงการเต็ม
    id: number;
    name: string;
    address?: string;
  };
  
  // ข้อมูลอุปกรณ์และซิม
  product_id: number;            // รหัสสินค้า
  product_name?: string;         // ชื่อสินค้า (จาก backend)
  product?: {                    // ข้อมูลสินค้าเต็ม
    id: number;
    name: string;
    code: string;
  };
  device_id?: number;            // รหัสอุปกรณ์
  device_code?: string;          // รหัสอุปกรณ์ (จาก backend)
  device_name?: string;          // ชื่ออุปกรณ์ (จาก backend, สำหรับแสดงผล)
  device?: {                     // ข้อมูลอุปกรณ์เต็ม
    id: number;
    serial_number: string;
    name: string;
  };
  sim_id?: number;               // รหัสซิม
  sim_number?: string;           // เบอร์ซิม (จาก backend)
  
  // ข้อมูลการเงิน — สถานะ/วันที่มัดจำอยู่ระดับ rental_items (ดู RentalItem) ไม่มีที่ระดับ rental
  deposit_amount?: number;       // จำนวนเงินมัดจำรวม (aggregate)
  monthly_fee?: number;          // ค่าเช่ารายเดือน
  total_amount?: number;         // จำนวนเงินรวม
  
  // ข้อมูลสถานะและหมายเหตุ
  status: RentalStatus;          // สถานะการเช่า
  notes?: string;                // หมายเหตุ
  
  // ข้อมูลสัญญา
  contract_id?: number;          // รหัสสัญญา (ถ้ามี)
  contract_number?: string;      // เลขที่สัญญา (จาก backend)
  
  // ข้อมูลการสร้างและแก้ไข
  created_by_user_id?: number;      // รหัสผู้สร้าง
  approved_by_user_id?: number;     // รหัสผู้อนุมัติ
  created_at: string;
  updated_at?: string;
  
  // ข้อมูลเพิ่มเติม
  items_count?: number;            // จำนวนรายการอุปกรณ์ในการเช่า

  // rental_items array (ถ้า API ส่งมา)
  rental_items?: RentalItem[];
  
  // ข้อมูลการต่อเช่า
  extension_count?: number;        // จำนวนครั้งที่ต่อ (เฉพาะอนุมัติแล้ว)
  // S122-EXTQUEUE (PO 2026-08-19): จำนวนคำขอต่อทุกสถานะ (pending+approved+rejected)
  extension_request_count?: number;
  total_extension_days?: number;   // จำนวนวันที่ต่อรวม
  original_end_date?: string;      // วันสิ้นสุดเดิม (ก่อนต่อ)
  is_extension_allowed?: boolean;  // อนุญาตให้ต่อได้หรือไม่

  // New fields for data migration
  delivery_date?: string;          // วันที่ส่งมอบอุปกรณ์
}

// ข้อมูลสำหรับการสร้างหรือแก้ไขการเช่า
export interface RentalForm {
  code?: string;
  rental_type: RentalType;
  rental_date: string;
  billing_start_date?: string;
  period_days: number;
  site_id: number;
  customer_id?: number;
  company_id: number;
  product_id: number;
  device_id?: number;
  sim_id?: number;
  deposit_amount?: number;
  monthly_fee?: number;
  status?: RentalStatus;
  notes?: string;

  // New fields for data migration
  delivery_date?: string;
}

// พารามิเตอร์สำหรับการดึงข้อมูลการเช่า
export interface GetRentalsParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: RentalStatus;
  rental_type?: RentalType;
  company_id?: number;
  site_id?: number;
  customer_id?: number;
  product_id?: number;
  device_id?: number;
  sim_id?: number;
  contract_id?: number;
  without_contract?: boolean;
  available_for_contract?: boolean; // สำหรับดึงรายการเช่าที่สามารถใช้สร้างสัญญาได้
  start_date?: string;
  end_date?: string;
  is_expired?: boolean;
  is_expiring_soon?: boolean;
  days_to_expiry?: number;
  // เพิ่มสำหรับการค้นหาขั้นสูง
  company_name?: string;
  site_name?: string;
  product_name?: string;
  device_code?: string;
  is_software_only?: boolean;
}

// การตอบกลับจากการดึงข้อมูลการเช่า
export interface GetRentalsResponse {
  items: Rental[];
  total: number;
  page: number;
  limit: number;
}

// รายการเช่าที่ถูกลบ (soft delete) — shape จาก GET /rentals/deleted/list
export interface DeletedRental {
  id: number;
  rental_number: string;
  rental_type?: string | null;
  status?: string | null;
  company_name?: string | null;
  site_name?: string | null;
  rental_date?: string | null;
  total_amount: number;
  deleted_at?: string | null;
  deleted_by?: string | null;
}

export interface GetDeletedRentalsResponse {
  items: DeletedRental[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// Helper function เพื่อแปลงค่าเป็น integer อย่างปลอดภัย
const safeParseInt = (value: any): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : parseInt(String(value), 10);
  return isNaN(parsed) ? undefined : parsed;
};

// บริการจัดการข้อมูลการเช่า
const rentalService = {
  // ดึงรายการเช่าทั้งหมด
  async getRentals(params: GetRentalsParams = {}): Promise<GetRentalsResponse> {
    // สร้าง query params และกรอง undefined ออก
    const queryParams: any = {
      page: params.page || 1,
      limit: params.limit || 10
    };

    // เพิ่มเฉพาะ params ที่มีค่า
    if (params.sort !== undefined) queryParams.sort = params.sort;
    if (params.order !== undefined) queryParams.order = params.order;
    if (params.search !== undefined) queryParams.search = params.search;
    if (params.status !== undefined) queryParams.status = params.status;
    if (params.rental_type !== undefined) queryParams.rental_type = params.rental_type;

    // ใช้ safeParseInt สำหรับ ID params เพื่อป้องกัน 422 error จาก invalid integer
    const companyId = safeParseInt(params.company_id);
    const siteId = safeParseInt(params.site_id);
    const customerId = safeParseInt(params.customer_id);
    const productId = safeParseInt(params.product_id);
    const deviceId = safeParseInt(params.device_id);
    const simId = safeParseInt(params.sim_id);
    const contractId = safeParseInt(params.contract_id);
    const daysToExpiry = safeParseInt(params.days_to_expiry);

    if (companyId !== undefined) queryParams.company_id = companyId;
    if (siteId !== undefined) queryParams.site_id = siteId;
    if (customerId !== undefined) queryParams.customer_id = customerId;
    if (productId !== undefined) queryParams.product_id = productId;
    if (deviceId !== undefined) queryParams.device_id = deviceId;
    if (simId !== undefined) queryParams.sim_id = simId;
    if (contractId !== undefined) queryParams.contract_id = contractId;
    if (daysToExpiry !== undefined) queryParams.days_to_expiry = daysToExpiry;

    if (params.without_contract !== undefined) queryParams.without_contract = params.without_contract;
    if (params.available_for_contract !== undefined) queryParams.available_for_contract = params.available_for_contract;
    if (params.start_date !== undefined) queryParams.start_date = params.start_date;
    if (params.end_date !== undefined) queryParams.end_date = params.end_date;
    if (params.is_expired !== undefined) queryParams.is_expired = params.is_expired;
    if (params.is_expiring_soon !== undefined) queryParams.is_expiring_soon = params.is_expiring_soon;
    // เพิ่ม parameters สำหรับการค้นหาขั้นสูง
    if (params.company_name !== undefined) queryParams.company_name = params.company_name;
    if (params.site_name !== undefined) queryParams.site_name = params.site_name;
    if (params.product_name !== undefined) queryParams.product_name = params.product_name;
    if (params.device_code !== undefined) queryParams.device_code = params.device_code;
    if (params.is_software_only !== undefined) queryParams.is_software_only = params.is_software_only;

    const response = await ApiWithLogging.get<GetRentalsResponse>('/rentals/', 
      { params: queryParams },
      {
        service: 'rentalService',
        action: 'view_rentals_list',
        entityType: 'rental'
      }
    );
    
    return response;
  },

  // ดึงการเช่าที่ใกล้หมดอายุ (ใช้ reports API แทน)
  async getExpiringRentals(days: number = 30): Promise<Rental[]> {
    try {
      // Backend ไม่มี /rentals/expiring ให้ใช้ /reports/rental-summary แทน
      const response = await ApiWithLogging.get('/reports/rental-summary', 
        { params: { expiring_days: days } },
        {
          service: 'rentalService',
          action: 'view_expiring_rentals',
          entityType: 'rental',
          details: { days }
        }
      );
      
      // อาจต้องปรับ response structure ตาม backend
      // ApiWithLogging already unwraps .data — access expiring_rentals directly on response
      return (response as any).expiring_rentals || [];
    } catch (error) {
      console.error('Error in getExpiringRentals service:', error);
      // Fallback: ใช้ active rentals แทน
      return [];
    }
  },

  // ดึงข้อมูลการเช่าตาม id
  async getRental(id: number): Promise<Rental> {
    const response = await ApiWithLogging.get<Rental>(`/rentals/${id}`, {},
      {
        service: 'rentalService',
        action: 'view_rental_detail',
        entityType: 'rental',
        entityId: id
      }
    );
    return response;
  },

  // ดึงข้อมูลการเช่าสำหรับการแก้ไข (รวม rental_items)
  async getRentalForEdit(id: number): Promise<any> {
    const response = await ApiWithLogging.get<any>(`/rentals/${id}/edit?t=${Date.now()}`, {},
      {
        service: 'rentalService',
        action: 'get_rental_for_edit',
        entityType: 'rental',
        entityId: id
      }
    );
    return response;
  },

  // สร้างการเช่าใหม่
  async createRental(data: any): Promise<Rental> {
    const response = await ApiWithLogging.post<Rental>('/rentals/', data, {},
      {
        service: 'rentalService',
        action: 'create_rental',
        entityType: 'rental',
        details: {
          rental_number: data.rental_number,
          company_id: data.company_id,
          site_id: data.site_id,
          items_count: data.rental_items?.length
        }
      }
    );
    return response;
  },

  // แก้ไขข้อมูลการเช่า
  // S1228: เพิ่มพารามิเตอร์ ifMatch (optional) — pass rental.updated_at จาก initial read
  // เพื่อ enable optimistic-concurrency check (BE จะ return 412 ถ้า stale)
  // ถ้าไม่ส่ง — ทำงานเหมือนเดิม (LWW backward-compatible)
  async updateRental(id: number, data: any, ifMatch?: string): Promise<Rental> {
    const config = ifMatch ? { headers: { 'If-Match': ifMatch } } : {};
    const response = await ApiWithLogging.put<Rental>(`/rentals/${id}`, data, config,
      {
        service: 'rentalService',
        action: 'update_rental',
        entityType: 'rental',
        entityId: id,
        details: {
          updated_fields: Object.keys(data),
          status: data.status,
          if_match_used: Boolean(ifMatch),
        }
      }
    );
    return response;
  },

  // ลบการเช่า (soft delete)
  async deleteRental(id: number): Promise<{success: boolean; message: string}> {
    const response = await ApiWithLogging.delete<{success: boolean; message: string}>(`/rentals/${id}/soft`, {},
      {
        service: 'rentalService',
        action: 'delete_rental',
        entityType: 'rental',
        entityId: id
      }
    );
    return response;
  },

  // ดึงรายการเช่าที่ถูกลบ (soft delete) — ใช้กับจอ "รายการที่ลบ"
  async getDeletedRentals(page: number = 1, limit: number = 10): Promise<GetDeletedRentalsResponse> {
    const response = await ApiWithLogging.get<GetDeletedRentalsResponse>('/rentals/deleted/list',
      { params: { page, limit } },
      {
        service: 'rentalService',
        action: 'view_deleted_rentals',
        entityType: 'rental'
      }
    );
    return response;
  },

  // กู้คืนรายการเช่าที่ถูกลบ
  async restoreRental(id: number): Promise<{success: boolean; message: string}> {
    const response = await ApiWithLogging.post<{success: boolean; message: string}>(`/rentals/${id}/restore`, {}, {},
      {
        service: 'rentalService',
        action: 'restore_rental',
        entityType: 'rental',
        entityId: id
      }
    );
    return response;
  },

  // เปลี่ยนสถานะการเช่า
  async changeStatus(id: number, status: RentalStatus): Promise<Rental> {
    const response = await ApiWithLogging.put<Rental>(`/rentals/${id}/status`, { status }, {},
      {
        service: 'rentalService',
        action: 'change_rental_status',
        entityType: 'rental',
        entityId: id,
        details: { status }
      }
    );
    return response;
  },

  // อัพเดทสถานะการเช่า (alias สำหรับ changeStatus - ใช้กับ useRentalMutations)
  // C7 (MANUAL_AUDIT_SYSTEM_FINDINGS): โมดัล "อัพเดตสถานะหลายรายการ" บังคับให้ผู้ใช้กรอก
  // "เหตุผล" (≥10 ตัวอักษร) + "วันที่มีผล" แต่เดิมค่าที่กรอกถูกโยนทิ้งทั้งคู่ ไม่ถูกเก็บที่ใดเลย
  // → ตรวจย้อนหลังไม่ได้ว่าใครเปลี่ยนสถานะเพราะอะไร · backend รับ reason/effective_date แล้ว
  // (endpoints/rentals.py RentalStatusUpdate) และเขียนต่อท้ายข้อความ activity log ให้
  // หมายเหตุ: "วันที่มีผล" เก็บเป็นหลักฐานใน audit เท่านั้น ยังไม่ย้อนวันตัดบิล/วันคืนของจริง
  async updateRentalStatus(
    id: number,
    status: string,
    reason?: string,
    effectiveDate?: string,
    // "ของกลับแล้วหรือยัง" — ตอบตรงจุดที่กดยกเลิก/ปิดสัญญา (ไม่ต้องไปหน้าติดตามการคืน)
    // true = ระบบบันทึกรับคืนในใบตามเก็บให้แล้วปล่อยของกลับคลังทันที
    goodsReceived?: { equipment_returned: boolean; equipment_condition?: 'GOOD' | 'DAMAGED' | 'LOST' }
  ): Promise<Rental> {
    const body: {
      status: string; reason?: string; effective_date?: string;
      equipment_returned?: boolean; equipment_condition?: string;
    } = { status };
    // ส่งเฉพาะค่าที่กรอกมาจริง — ไม่ยัด null/'' ทับ (บทเรียน C8)
    if (reason && reason.trim()) body.reason = reason.trim();
    if (effectiveDate) body.effective_date = effectiveDate;
    if (goodsReceived) {
      body.equipment_returned = goodsReceived.equipment_returned;
      if (goodsReceived.equipment_condition) body.equipment_condition = goodsReceived.equipment_condition;
    }

    const response = await ApiWithLogging.put<Rental>(`/rentals/${id}/status`, body, {},
      {
        service: 'rentalService',
        action: 'update_rental_status',
        entityType: 'rental',
        entityId: id,
        details: { status, reason: body.reason, effective_date: body.effective_date }
      }
    );
    return response;
  },

  // ยกเลิกการเช่า
  async cancelRental(id: number, reason?: string): Promise<Rental> {
    const response = await ApiWithLogging.post<Rental>(`/rentals/${id}/cancel`, { reason }, {},
      {
        service: 'rentalService',
        action: 'cancel_rental',
        entityType: 'rental',
        entityId: id,
        details: { reason }
      }
    );
    return response;
  },

  // อุปกรณ์ของสัญญานี้ที่ยังไม่ได้รับคืน (ใช้โชว์ของค้างบนหน้ารายละเอียดการเช่า)
  async getOutstandingReturns(id: number): Promise<OutstandingReturns> {
    return await ApiWithLogging.get(`/rentals/${id}/outstanding-returns`, {},
      {
        service: 'rentalService',
        action: 'get_outstanding_returns',
        entityType: 'rental',
        entityId: id,
      }
    );
  },

  // บันทึก "รับคืนอุปกรณ์แล้ว" โดยไม่เปลี่ยนสถานะสัญญา — ใช้กับสัญญาที่ยกเลิก/ปิดไปแล้วได้
  // (ปุ่ม "ของกลับครบแล้ว–ปิดการเช่า" ใช้ได้เฉพาะสัญญาที่ยังใช้งานอยู่ เพราะมันปิดสัญญาไปในตัว)
  async recordReturns(
    id: number,
    payload: {
      returned_item_ids?: number[];
      returned_items?: number[];
      equipment_condition?: 'GOOD' | 'DAMAGED' | 'LOST';
      condition_notes?: string;
      return_date?: string;
    } = {}
  ): Promise<Rental> {
    return await ApiWithLogging.post<Rental>(`/rentals/${id}/record-returns`, payload, {},
      {
        service: 'rentalService',
        action: 'record_rental_returns',
        entityType: 'rental',
        entityId: id,
        details: payload
      }
    );
  },

  // Fix ก (ก-1): สัญญา "รอการยกเลิก" — ประเมินว่าปิดใบแจ้งยกเลิกตอนนี้ได้ไหม (ของกลับครบหรือยัง)
  // ไม่มี 4xx ตอน "ยังปิดไม่ได้" — backend ตอบ 200 เสมอ พร้อม eligible=false + reason_code
  async getFinalizeCancellation(id: number): Promise<CancellationFinalizeStatus> {
    return await ApiWithLogging.get(`/rentals/${id}/finalize-cancellation`, {},
      {
        service: 'rentalService',
        action: 'get_finalize_cancellation_status',
        entityType: 'rental',
        entityId: id,
      }
    );
  },

  // Fix ก (ก-2): ปิดใบแจ้งยกเลิกทันที — สัญญา → ยกเลิกแล้ว, ยังคิดค่าเช่าถึงวันที่แจ้งไว้เดิม,
  // มัดจำที่ชำระแล้วเข้าคิว "รอคืนมัดจำ", อุปกรณ์/ซิมที่รับคืนแล้วใช้งานใหม่ได้ทันที
  async finalizeCancellation(id: number, note?: string): Promise<CancellationFinalizeResult> {
    return await ApiWithLogging.post<CancellationFinalizeResult>(`/rentals/${id}/finalize-cancellation`, { note }, {},
      {
        service: 'rentalService',
        action: 'finalize_cancellation',
        entityType: 'rental',
        entityId: id,
        details: { note }
      }
    );
  },

  // อนุมัติ/ปฏิเสธการเช่า (DRAFT → ACTIVE เมื่อ approved=true, → CANCELLED เมื่อ approved=false)
  async approveRental(id: number, approved: boolean = true, notes?: string): Promise<Rental> {
    const response = await ApiWithLogging.post<Rental>(`/rentals/${id}/approve`, { approved, notes }, {},
      {
        service: 'rentalService',
        action: approved ? 'approve_rental' : 'reject_rental',
        entityType: 'rental',
        entityId: id,
        details: { approved, notes }
      }
    );
    return response;
  },

  // ดึงประวัติการเช่า
  async getRentalHistory(id: number): Promise<any[]> {
    const response = await ApiWithLogging.get<any[]>(`/rentals/${id}/history`, {},
      {
        service: 'rentalService',
        action: 'view_rental_history',
        entityType: 'rental',
        entityId: id
      }
    );
    return response;
  },

  // แก้วันสิ้นสุดเก็บเงินของสัญญาที่จบแล้ว (COMPLETED/CANCELLED) — ต้องระบุเหตุผล · สิทธิ์ rentals.billing_manage
  async updateBillingEndDate(
    id: number,
    data: { billing_end_date: string; reason: string }
  ): Promise<RentalBillingEndDateResponse> {
    return await ApiWithLogging.patch<RentalBillingEndDateResponse>(`/rentals/${id}/billing-end-date`, data, {},
      {
        service: 'rentalService',
        action: 'update_rental_billing_end_date',
        entityType: 'rental',
        entityId: id,
        details: { billing_end_date: data.billing_end_date }
      }
    );
  },

  // อัพเดทสถานะหลายรายการ
  // BUG-RT-003 FIX: there is NO `PUT /rentals/bulk/status` backend route — that path was
  // captured by `PUT /rentals/{rental_id}/status` and returned 422 (int_parsing on "bulk").
  // Loop over the real single-status endpoint instead, so callers get correct per-row
  // behavior (incl. the RT-001/RT-002 billing_end_date + equipment-return guarantees).
  async bulkUpdateStatus(ids: number[], status: string): Promise<{success: boolean; updated: number; failed: number}> {
    let updated = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await this.updateRentalStatus(id, status);
        updated += 1;
      } catch {
        failed += 1;
      }
    }
    return { success: failed === 0, updated, failed };
  },

  // Export รายการเช่าเป็น Excel พร้อมข้อมูลละเอียดจาก Master Data
  async exportToExcel(params: GetRentalsParams = {}): Promise<void> {
    try {
      const queryParams = new URLSearchParams();

      // GAP-EXPORT-FILTER (ชั้นที่ 2): เดิมรับ params ครบแต่ **append ไม่ครบ** —
      // ตก rental_type / start_date / end_date และตัวกรองค้นหาขั้นสูงทั้ง 4 ตัว
      // ⇒ หน้าจอส่งมาถูกแล้ว แต่หายที่นี่เงียบ ๆ · ชั้นนี้ซ่อนที่สุดเพราะเทสต์ฝั่ง backend
      // จับไม่ได้เลย (backend ไม่เคยเห็น param ที่ไม่ถูกส่ง)
      // ใส่คีย์เฉพาะที่มีค่าจริง — ไม่กรอง = ไม่มี query string เลย (ไม่พังทางกลับ)
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.rental_type) queryParams.append('rental_type', params.rental_type);
      if (params.company_id) queryParams.append('company_id', params.company_id.toString());
      if (params.site_id) queryParams.append('site_id', params.site_id.toString());
      if (params.contract_id) queryParams.append('contract_id', params.contract_id.toString());
      if (params.without_contract !== undefined) queryParams.append('without_contract', params.without_contract.toString());
      if (params.is_expiring_soon !== undefined) queryParams.append('is_expiring_soon', params.is_expiring_soon.toString());
      if (params.days_to_expiry) queryParams.append('days_to_expiry', params.days_to_expiry.toString());
      if (params.is_software_only !== undefined) queryParams.append('is_software_only', params.is_software_only.toString());
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      // ค้นหาขั้นสูง (AdvancedSearchModal)
      if (params.company_name) queryParams.append('company_name', params.company_name);
      if (params.site_name) queryParams.append('site_name', params.site_name);
      if (params.product_name) queryParams.append('product_name', params.product_name);
      if (params.device_code) queryParams.append('device_code', params.device_code);

      // สร้าง URL
      const url = `/rentals/export/excel${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      // ดาวน์โหลดไฟล์
      const response = await ApiWithLogging.get(url, {
        responseType: 'blob'
      }, {
        service: 'rentalService',
        action: 'export_rentals_excel',
        entityType: 'rental',
        details: {
          filters: params
        }
      });

      // สร้าง blob URL และ download
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      // ใช้วันที่ท้องถิ่น (ไทย) ไม่ใช่ UTC — toISOString() หลังเที่ยงคืนได้ชื่อไฟล์เป็นวันเมื่อวาน
      // (กติกาเดิมของโปรเจกต์ E2E v6 OBS-4a — หน้าอื่นใช้ getLocalDateString แล้ว)
      link.download = `rentals_export_${getLocalDateString()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error: any) {
      console.error('Error exporting rentals to Excel:', error);
      throw error;
    }
  },

  /**
   * FB-2026-0017 พิมพ์ใบส่งของ (มติ PO 3 พ.ย. 2026) — POST /rentals/delivery-notes/pdf
   * ได้ PDF ไฟล์เดียว 1 ใบต่อ (บริษัท + โครงการ) · ไม่มีราคา · backend บันทึกประวัติการพิมพ์ให้เอง
   * error มาเป็น Blob (responseType blob) → ผู้เรียกใช้ `getApiErrorMessageAsync` อ่านข้อความไทย
   */
  async downloadDeliveryNotes(rentalIds: number[]): Promise<void> {
    const blobData = await ApiWithLogging.post<Blob>(
      '/rentals/delivery-notes/pdf',
      { rental_ids: rentalIds },
      { responseType: 'blob' },
      {
        service: 'rentalService',
        action: 'print_delivery_notes',
        entityType: 'rental',
        details: { rental_ids: rentalIds },
      },
    );
    const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'application/pdf' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `delivery_note_${getLocalDateString()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // ตรวจสอบว่ารหัสการเช่านี้มีอยู่แล้วหรือไม่
  async checkCodeExists(code: string): Promise<boolean> {
    try {
      const response = await ApiWithLogging.get<GetRentalsResponse>('/rentals/', 
        { params: { search: code, limit: 100 } },
        {
          service: 'rentalService',
          action: 'check_code_exists',
          entityType: 'rental',
          details: { code }
        }
      );
      
            // KD3 S2126 (BUG-S2126-001): search ค้นหลายช่องแบบ substring → เทียบรหัสตรงตัวเอง
      // (ไม่สนตัวพิมพ์ตาม collation ของ DB) · ดึงหลายแถวไม่งั้นแถวที่ตรงจริงอาจไม่อยู่หน้าแรก
      return isExactCodeMatch(response.items.map(r => r.rental_number), code);
    } catch (error) {
      console.error('Error checking rental code exists:', error);
      return false;
    }
  }
};

// Named exports สำหรับ React Query hooks
export const getRentals = rentalService.getRentals;
export const getRental = rentalService.getRental;
export const createRental = rentalService.createRental;
export const updateRental = rentalService.updateRental;
export const deleteRental = rentalService.deleteRental;
export const getRentalHistory = rentalService.getRentalHistory;
export const bulkUpdateStatus = rentalService.bulkUpdateStatus;

export default rentalService;